/**
 * Parallax logos: a set of logos or avatars that float over a section and move with the scroll.
 *
 * Any section can use it, and a page can hold several: each `.js-parallaxLogosContainer` gets its
 * own state, and every `img` inside it is one logo. The container is the scroll subject, so the
 * logos travel while the container crosses the viewport. The logos are placed by the section's own
 * CSS; this behavior only adds motion on top of that placement.
 *
 * Everything moves with the scroll and nothing else: at rest the scene holds still. Two layers,
 * each on its own transform channel so they compose on one element:
 * - drift (`translate`): every logo moves against the scroll at its own speed, vertically only: a sideways bloom or a scale that peaks mid-travel reads as approach and retreat, and competes with the one cue that carries the depth, how far each plane travels;
 * - roll (`rotate`): a faint tilt that turns over the travel, alternating direction along the row.
 * The entrance (`js-parallaxLogosScaleIn` on the container) opens the scene once the section is
 * well into the viewport: at load above the fold, on scroll below it. Every logo on screen at that
 * moment is the intro: it waits for the figure they float over, holds until the scene has settled,
 * and rises in a slow cascade from the highest down. A logo may sit near the container's edge and
 * start its drift beyond it, so the intro does not wait for it: each logo still off screen fades in
 * quickly the first time enough of it is on screen. Ship the logos hidden with `opacity-0` so they
 * do not flash before the script runs. A batch waits for its images to decode before it moves: a
 * fade on an image without pixels shows nothing, and the pixels then land without the fade.
 *
 * A section may also mark two deeper planes. `.js-parallaxBackdrop` is the farthest: it drifts the
 * other way from the logos, and the section's CSS scales it up so the drift never uncovers an edge. `.js-parallaxFigure` sits between the two: a cut-out subject that drifts
 * a little the same way as the backdrop, at its natural scale, so it stays crisp and reads as the
 * anchor of the scene. The figure is cut flat at its lower edge, so the section's CSS rests it a
 * little below the section, by at least the figure's drift.
 *
 * Markup contract: a `.js-parallaxLogosContainer`, optionally `.js-parallaxLogosScaleIn`, whose
 * `img` children carry `data-speed`, the strength of every layer as a percent (100 = the full
 * range; 50 when absent). The backdrop and the figure are looked up in the container's section.
 */

import {getBreakpointName} from '../modules/breakpoint.js';
import {createRun} from '../modules/motion/run.js';
import {animateOnScroll} from '../modules/motion/scrollDrivenAnimation.js';
import {resolveStaggerOptions, staggerReveal} from '../modules/motion/staggerReveal.js';

/** Drift of a speed-100 logo in px, each way. A phone scrolls a shorter banner, so it gets a shorter drift. */
const RANGE_PX = Object.freeze({mobile: 36, tablet: 60, laptop: 120, desktop: 120});
const DEFAULT_SPEED = 50;
/**
 * One inertia for every plane: two thirds of the way in this time, at rest after about four times
 * it. The planes are one camera move, so they must ease together; a plane with a lag of its own
 * falls out of step with the others while the scroll speeds up or stops, and the depth order wobbles.
 * Every drift is anchored: a section above the fold is painted at rest before the script runs,
 * and must not move until the user scrolls.
 */
const DRIFT_OPTIONS = Object.freeze({lag: 280, anchor: true});
/** Roll over the travel in degrees each way for a speed-100 logo; faces must stay upright enough to read. */
const ROLL_DEG = 2;
/**
 * The backdrop drifts with the scroll, against the logos, four times as far as the figure. The
 * section's CSS scales the backdrop up by 1.2 at rest, which adds a tenth of its height at each
 * edge, so a drift of a smaller share than that can never uncover one. The scale stays in CSS
 * and out of the keyframes: the driver seeks its first frame after the page has painted, and a
 * scale that only exists in the animation would snap in at that moment.
 */
const BACKDROP_DRIFT_SHARE = 0.08;
/** The figure drifts a little with the backdrop, never scaled: it is the plane the eye holds on to. */
const FIGURE_DRIFT_SHARE = 0.02;
/** The intro opens once the container reaches the lower edge of this share of the viewport, counted from the top. */
const INTRO_VIEWPORT_SHARE = 0.6;
/** Share of a late logo that must be on screen before it fades in. */
const ENTRANCE_VISIBLE_SHARE = 0.15;
const HIDDEN_CLASS = 'opacity-0';
/** The viewer is looking at the frame: a lead-in lets the backdrop, the figure, and the heading settle, then the logos rise one by one. */
const INTRO_ENTRANCE = Object.freeze({animationType: 'rise', duration: 400, staggerDelay: 60, startDelay: 400});
/** Short and plain: a late logo is in place well inside half a second, so it does not lag the scene it joins. */
const LATE_ENTRANCE = Object.freeze({animationType: 'fade-in', duration: 160, staggerDelay: 30});

/**
 * @typedef {object} Logo
 * @property {HTMLImageElement} element The image.
 * @property {number} strength `data-speed` / 100.
 * @property {number} roll Direction of the roll: 1 or -1, alternating along the row.
 * @property {boolean} revealed Whether the entrance started, or was never wanted.
 */

/**
 * @typedef {object} ParallaxState
 * @property {Logo[]} logos The images in markup order.
 * @property {import('../modules/motion/run.js').Run} drifts The drift and the roll of every logo, and the drifts of the deeper planes; rebuilt when the breakpoint changes.
 * @property {import('../modules/motion/run.js').Run} entrances The pop-in animations.
 * @property {string} breakpoint The breakpoint name the drifts were built for.
 * @property {HTMLElement|null} backdrop The section's farthest plane, or null when it has none.
 * @property {HTMLElement|null} figure The section's cut-out subject in front of the backdrop, or null when it has none.
 * @property {IntersectionObserver|null} introObserver Opens the scene once the container is well into the viewport; null without an entrance.
 * @property {IntersectionObserver|null} entranceObserver Fades each late logo in once it is on screen; null without an entrance.
 * @property {AbortController} controller Aborts the listeners on cleanup.
 */

/** @type {WeakMap<HTMLElement, ParallaxState>} */
const states = new WeakMap();

/**
 * @param {HTMLElement} container
 * @param {ParallaxState} state
 * @returns {void}
 */
function startScrollAnimations(container, state) {
    const range = RANGE_PX[state.breakpoint];
    state.drifts = createRun();
    state.drifts.add(...state.logos.map(({element, strength, roll}) => {
        const options = {...DRIFT_OPTIONS, subject: container};
        const drift = strength * range;
        const tilt = roll * ROLL_DEG * strength;

        // One animation per logo: both channels share the subject and the lag, and the driver seeks half as many clocks per frame.
        return animateOnScroll(element, [{translate: `0 ${drift}px`, rotate: `${-tilt}deg`}, {translate: `0 ${-drift}px`, rotate: `${tilt}deg`}], options);
    }));

    if (state.backdrop) {
        const drift = Math.round(state.backdrop.getBoundingClientRect().height * BACKDROP_DRIFT_SHARE);
        state.drifts.add(animateOnScroll(state.backdrop, [{translate: `0 ${-drift}px`}, {translate: `0 ${drift}px`}], {...DRIFT_OPTIONS, subject: container}));
    }
    if (state.figure) {
        const drift = Math.round(state.figure.getBoundingClientRect().height * FIGURE_DRIFT_SHARE);
        state.drifts.add(animateOnScroll(state.figure, [{translate: `0 ${-drift}px`}, {translate: `0 ${drift}px`}], {...DRIFT_OPTIONS, subject: container}));
    }
}

/**
 * Wait for the images to hold pixels. A broken image settles too: it shows its broken state
 * instead of staying hidden, so a missing asset is seen, not masked.
 * @param {HTMLImageElement[]} images
 * @returns {Promise<void>}
 */
function decoded(images) {
    return Promise.allSettled(images.map((image) => image.decode())).then(() => {});
}

/**
 * Reveal a batch of logos in the given order, once their images are ready.
 * @param {ParallaxState} state
 * @param {Logo[]} batch
 * @param {boolean} intro Whether this batch opens the scene.
 * @returns {Promise<void>}
 */
async function reveal(state, batch, intro) {
    for (const logo of batch) {
        logo.revealed = true;
    }
    const options = resolveStaggerOptions(intro ? INTRO_ENTRANCE : LATE_ENTRANCE);
    if (options === null) {
        throw new Error('Parallax logos: the entrance must resolve to an animation.');
    }

    const elements = batch.map(({element}) => element);
    // The intro never shows a logo before the figure it floats over.
    await decoded(intro && state.figure instanceof HTMLImageElement ? [...elements, state.figure] : elements);
    if (state.controller.signal.aborted) {
        return;
    }

    // Same task as the animation start, so the class removal and the first keyframe land in one style recalc.
    for (const logo of batch) {
        logo.element.classList.remove(HIDDEN_CLASS);
    }
    state.entrances.add(...staggerReveal(elements, options, () => {}));
}

/**
 * @param {Logo[]} logos
 * @returns {Logo[]} The logos with any part on screen, the highest first.
 */
function onScreen(logos) {
    return logos
        .map((logo) => ({logo, top: logo.element.getBoundingClientRect().top, bottom: logo.element.getBoundingClientRect().bottom}))
        .filter(({top, bottom}) => bottom > 0 && top < window.innerHeight)
        .sort((a, b) => a.top - b.top)
        .map(({logo}) => logo);
}

/**
 * Fades in each late logo once it is on screen; observes nothing until the intro has chosen its batch.
 * @param {ParallaxState} state
 * @returns {IntersectionObserver}
 */
function createEntranceObserver(state) {
    const observer = new IntersectionObserver((entries) => {
        if (state.controller.signal.aborted) {
            return;
        }
        const arrived = entries
            .filter((entry) => entry.isIntersecting)
            // The logo highest on screen pops first: it has been the closest to view the longest.
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
            .map((entry) => state.logos.find((logo) => logo.element === entry.target))
            .filter((logo) => logo !== undefined && !logo.revealed);
        if (arrived.length === 0) {
            return;
        }
        for (const logo of arrived) {
            observer.unobserve(logo.element);
        }
        void reveal(state, arrived, false);
    }, {threshold: ENTRANCE_VISIBLE_SHARE});

    return observer;
}

/**
 * Opens the scene once: the logos on screen rise together, the rest wait for their own arrival.
 * @param {HTMLElement} container
 * @param {ParallaxState} state
 * @returns {IntersectionObserver}
 */
function createIntroObserver(container, state) {
    const observer = new IntersectionObserver(([entry]) => {
        if (state.controller.signal.aborted || !entry.isIntersecting) {
            return;
        }
        observer.disconnect();
        const batch = onScreen(state.logos);
        for (const logo of state.logos) {
            if (!batch.includes(logo)) {
                state.entranceObserver?.observe(logo.element);
            }
        }
        if (batch.length > 0) {
            void reveal(state, batch, true);
        }
    }, {rootMargin: `0px 0px -${Math.round((1 - INTRO_VIEWPORT_SHARE) * 100)}% 0px`});
    observer.observe(container);

    return observer;
}

/**
 * @param {HTMLElement} container
 * @returns {void}
 */
function initializeParallaxLogos(container) {
    if (states.has(container)) {
        return;
    }

    const shouldScaleIn = container.classList.contains('js-parallaxLogosScaleIn');
    const logos = [...container.querySelectorAll('img')]
        .filter((element) => element instanceof HTMLImageElement)
        .map((element, index) => ({
            element,
            strength: Number(element.dataset.speed ?? DEFAULT_SPEED) / 100,
            roll: index % 2 === 0 ? 1 : -1,
            revealed: !shouldScaleIn,
        }));
    const controller = new AbortController();

    /** @type {ParallaxState} */
    const state = {
        logos,
        backdrop: container.closest('section')?.querySelector('.js-parallaxBackdrop') ?? null,
        figure: container.closest('section')?.querySelector('.js-parallaxFigure') ?? null,
        drifts: createRun(),
        entrances: createRun(),
        breakpoint: getBreakpointName(),
        introObserver: null,
        entranceObserver: null,
        controller,
    };
    states.set(container, state);

    if (shouldScaleIn) {
        for (const {element} of logos) {
            element.classList.add(HIDDEN_CLASS);
        }
        state.entranceObserver = createEntranceObserver(state);
        state.introObserver = createIntroObserver(container, state);
    }
    startScrollAnimations(container, state);

    window.addEventListener('resize', () => {
        const breakpoint = getBreakpointName();
        if (breakpoint === state.breakpoint) {
            return;
        }
        state.breakpoint = breakpoint;
        state.drifts.cancel();
        startScrollAnimations(container, state);
    }, {passive: true, signal: controller.signal});
}

/**
 * Stop every layer and show the logos at rest.
 * @param {HTMLElement} container
 * @returns {void}
 */
export function cleanupParallaxLogos(container) {
    const state = states.get(container);
    if (!state) {
        return;
    }

    state.controller.abort();
    state.introObserver?.disconnect();
    state.entranceObserver?.disconnect();
    state.drifts.cancel();
    state.entrances.cancel();
    for (const {element} of state.logos) {
        element.classList.remove(HIDDEN_CLASS);
    }
    states.delete(container);
}

/**
 * Initialize every parallax container that is not initialized yet. Safe to call after content swaps.
 * @returns {void}
 */
export function initializeComponent() {
    for (const container of document.querySelectorAll('.js-parallaxLogosContainer')) {
        if (container instanceof HTMLElement) {
            initializeParallaxLogos(container);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}
document.addEventListener('htmx:load', initializeComponent);
document.addEventListener('htmx:beforeCleanupElement', (event) => {
    if (event.target instanceof HTMLElement) {
        cleanupParallaxLogos(event.target);
    }
});
